import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import {
  AlertTriangle, Sparkles, FileDown, RefreshCw, Loader2, Archive,
  CheckCircle2, XCircle, MessageSquare, Wand2,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  refreshRedFlags, listRedFlags, resolveRedFlag,
  runReconcile, getReconcileStatus, reprocessStoredZips,
  getReportPreview, startReportJob, getReportJob, downloadLatestReportDocx, goldStandardCompare,
} from '../api/client';

const CAT_STYLE = {
  critical:           'border-red-500/50 bg-red-500/10 text-red-300',
  evidence_gap:       'border-orange-500/50 bg-orange-500/10 text-orange-300',
  conflict:           'border-red-500/50 bg-red-500/10 text-red-300',
  calculation_check:  'border-amber-500/50 bg-amber-500/10 text-amber-300',
  metadata_correction:'border-cyan-500/50 bg-cyan-500/10 text-cyan-300',
  assumption_review:  'border-slate-600 bg-slate-800/60 text-slate-300',
  ai_low_confidence:  'border-purple-500/50 bg-purple-500/10 text-purple-300',
};

const CAT_LABEL = {
  critical: 'CRITICAL',
  evidence_gap: 'EVIDENCE GAP',
  conflict: 'CONFLICT',
  calculation_check: 'CALCULATION',
  metadata_correction: 'METADATA',
  assumption_review: 'ASSUMPTION',
  ai_low_confidence: 'AI LOW CONFIDENCE',
};

export const WorkbenchPanel = ({ projectId }) => {
  const [flags, setFlags] = useState([]);
  const [reconLoading, setReconLoading] = useState(false);
  const [reconProgress, setReconProgress] = useState(null); // {processed, total, matches_created, current_reference, status}
  const [flagsLoading, setFlagsLoading] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [zipLoading, setZipLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [compareResult, setCompareResult] = useState(null);
  const [reconSummary, setReconSummary] = useState(null);

  const loadFlags = async () => {
    try {
      const r = await listRedFlags(projectId);
      setFlags(r || []);
    } catch (e) {
      // silent
    }
  };
  const loadPreview = async () => {
    try {
      const p = await getReportPreview(projectId);
      setPreview(p);
    } catch (e) {}
  };

  // Resume in-progress job on mount (if user reloads mid-reconcile).
  useEffect(() => {
    loadFlags();
    loadPreview();
    (async () => {
      try {
        const s = await getReconcileStatus(projectId);
        if (s && (s.status === 'queued' || s.status === 'running')) {
          setReconLoading(true);
          setReconProgress(s);
          // Rejoin the poll loop for an already-running server job.
          void resumePoll(s.id);
        }
      } catch (e) { /* no prior job */ }
    })();
  }, [projectId]); // eslint-disable-line

  const resumePoll = async (jobId) => {
    // Poll until terminal, then hydrate summary.
    // eslint-disable-next-line no-constant-condition
    while (true) {
      await new Promise((r) => setTimeout(r, 2000));
      let s;
      try { s = await getReconcileStatus(projectId, jobId); } catch (e) { continue; }
      setReconProgress(s);
      if (s.status === 'done') {
        setReconSummary(s.result || null);
        setReconLoading(false);
        try { await refreshRedFlags(projectId); } catch (e) {}
        await loadFlags();
        await loadPreview();
        const v = s.result?.vision || {};
        const totalPhoto = (v.matches_created || 0) + (v.cached_matches_reinserted || 0);
        toast.success(`AI reconciliation done — ${totalPhoto} photo + ${s.result?.research?.research_links_proposed || 0} research links`);
        return;
      }
      if (s.status === 'failed') {
        setReconLoading(false);
        toast.error('Reconciliation failed: ' + (s.error || 'unknown'));
        return;
      }
    }
  };

  const doReprocessZips = async () => {
    setZipLoading(true);
    try {
      const r = await reprocessStoredZips(projectId);
      if (r.status === 'no_zip_on_project') {
        toast.info('No ZIP archive stored on this project — upload one first.');
      } else {
        const totals = (r.reports || []).reduce((a, rr) => ({
          stored: a.stored + (rr.entries_stored || 0),
          dup: a.dup + (rr.duplicates_skipped || 0),
        }), { stored: 0, dup: 0 });
        toast.success(`Reprocessed ${r.archives_processed} archive(s) — ${totals.stored} new photos, ${totals.dup} duplicates skipped`);
      }
    } catch (e) {
      toast.error('Reprocess failed: ' + (e.response?.data?.detail || e.message));
    } finally { setZipLoading(false); }
  };

  const doReconcile = async () => {
    setReconLoading(true);
    setReconProgress(null);
    setReconSummary(null);
    try {
      const final = await runReconcile(projectId, {
        onProgress: (s) => setReconProgress(s),
      });
      setReconSummary(final.result || null);
      const v = final.result?.vision || {};
      const totalPhoto = (v.matches_created || 0) + (v.cached_matches_reinserted || 0);
      const purged = v.orphan_links_purged ? ` · purged ${v.orphan_links_purged} stale links` : '';
      toast.success(`AI reconciliation: ${totalPhoto} photo + ${final.result?.research?.research_links_proposed || 0} research links${purged}`);
      await refreshRedFlags(projectId);
      await loadFlags();
      await loadPreview();
    } catch (e) {
      toast.error('Reconciliation failed: ' + (e.response?.data?.detail || e.message));
    } finally { setReconLoading(false); }
  };

  const doRefreshFlags = async () => {
    setFlagsLoading(true);
    try {
      const r = await refreshRedFlags(projectId);
      setFlags(r.flags || []);
      toast.success(`Refreshed — ${r.flag_count} active flags`);
    } catch (e) {
      toast.error('Flag refresh failed');
    } finally { setFlagsLoading(false); }
  };

  const doResolve = async (flag, resolution) => {
    const rationale = resolution === 'dismissed'
      ? window.prompt('Rationale for dismissing (required):') || ''
      : '';
    if (resolution === 'dismissed' && !rationale) return;
    try {
      await resolveRedFlag(projectId, flag.id, resolution, rationale);
      toast.success(`Flag ${resolution}`);
      await loadFlags();
    } catch (e) {
      toast.error('Resolve failed');
    }
  };

  const saveBlob = (blob, name) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
  };

  const blobErrorDetail = async (e, fallback) => {
    try {
      const txt = await e.response.data.text();
      return JSON.parse(txt).detail || fallback;
    } catch (_) { return fallback; }
  };

  const doDownloadReport = async () => {
    if (reconLoading) {
      toast.error('Stage 4 AI reconciliation is still running. Wait for it to finish, then generate the report.');
      return;
    }
    setReportLoading(true);
    try {
      let job = await startReportJob(projectId);
      while (job.status === 'queued' || job.status === 'running') {
        await new Promise((r) => setTimeout(r, 3000));
        job = await getReportJob(projectId, job.id);
      }
      if (job.status !== 'done') throw new Error(job.error || 'Report generation failed');
      const blob = await downloadLatestReportDocx(projectId);
      saveBlob(blob, job.result?.filename || 'NDEA Evidence Report.docx');
      toast.success('Report generated, checked and saved to this project');
    } catch (e) {
      const detail = e.response ? e.response.data?.detail : e.message;
      toast.error(detail || 'Report generation failed', { duration: 12000 });
    } finally { setReportLoading(false); }
  };

  const doDownloadLatest = async () => {
    try {
      const blob = await downloadLatestReportDocx(projectId);
      saveBlob(blob, 'NDEA Evidence Report (saved).docx');
    } catch (e) {
      toast.error(await blobErrorDetail(e, 'No saved report yet'));
    }
  };

  const doCompare = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const r = await goldStandardCompare(projectId, file);
      setCompareResult(r);
      toast.success('Comparison complete');
    } catch (err) {
      toast.error('Compare failed');
    } finally { e.target.value = ''; }
  };

  const active = flags.filter((f) => f.resolution_status === 'open');
  const byCat = active.reduce((m, f) => { m[f.category] = (m[f.category] || 0) + 1; return m; }, {});

  return (
    <section className="space-y-6" data-testid="workbench-panel">
      {/* Action bar */}
      <div className="flex flex-wrap gap-2 items-center">
        <Button size="sm" variant="outline" onClick={doReprocessZips} disabled={zipLoading} data-testid="reprocess-zips-btn" className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800">
          {zipLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Archive className="w-4 h-4 mr-2" />}
          Reprocess stored ZIP
        </Button>
        <Button size="sm" onClick={doReconcile} disabled={reconLoading} data-testid="reconcile-btn" className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-medium">
          {reconLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}
          Run Stage 4 AI reconciliation
        </Button>
        <Button size="sm" variant="outline" onClick={doRefreshFlags} disabled={flagsLoading} data-testid="refresh-flags-btn" className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800">
          {flagsLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          Refresh red flags
        </Button>
        <Button size="sm" onClick={doDownloadReport} disabled={reportLoading || reconLoading} data-testid="download-report-btn" className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-medium">
          {reportLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
          Generate Evidence File (DOCX)
        </Button>
        <Button size="sm" variant="outline" onClick={doDownloadLatest} data-testid="download-saved-report-btn" className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800">
          <FileDown className="w-4 h-4 mr-2" />
          Download saved report
        </Button>
        <label className="text-[11px] uppercase tracking-widest text-slate-500 border border-slate-700 bg-slate-900 hover:bg-slate-800 px-2 py-1.5 rounded cursor-pointer inline-flex items-center gap-1">
          <Sparkles className="w-3.5 h-3.5 text-cyan-400" />
          Compare to reference
          <input type="file" accept=".docx" onChange={doCompare} className="hidden" data-testid="gold-standard-input" />
        </label>
      </div>

      {/* Reconcile progress bar (while a job is queued/running) */}
      {reconLoading && reconProgress && (
        <div className="rounded-lg border border-cyan-500/30 bg-cyan-500/5 p-3" data-testid="reconcile-progress">
          <div className="flex items-center justify-between text-xs text-slate-300 mb-1.5">
            <div className="flex items-center gap-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin text-cyan-400" />
              <span className="font-semibold text-cyan-300">Stage 4 AI reconciliation running</span>
              {reconProgress.current_reference && (
                <span className="font-mono text-slate-500">· {reconProgress.current_reference}</span>
              )}
            </div>
            <span className="font-mono text-cyan-300">
              {reconProgress.processed || 0} / {reconProgress.total || 0}
              {reconProgress.matches_created ? ` · ${reconProgress.matches_created} links` : ''}
            </span>
          </div>
          <div className="w-full h-2 rounded-full bg-slate-800 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-cyan-500 to-emerald-500 transition-all"
              style={{ width: `${reconProgress.total ? Math.min(100, Math.round((reconProgress.processed / reconProgress.total) * 100)) : 5}%` }}
            />
          </div>
          <div className="mt-1.5 text-[10px] text-slate-500">
            You can safely close this tab — the job continues on the server and this bar will resume when you come back.
          </div>
        </div>
      )}

      {/* Reconcile summary */}
      {reconSummary && (
        <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3 text-sm">
          <div className="font-semibold text-emerald-300 flex items-center gap-2">
            <Sparkles className="w-4 h-4" /> Stage 4 last run
          </div>
          <div className="mt-1 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs text-slate-300">
            <div>Photos processed: <span className="font-mono text-emerald-300">{reconSummary.vision?.processed || 0}/{reconSummary.vision?.photos_total || 0}</span></div>
            <div>Photo links: <span className="font-mono text-emerald-300">{(reconSummary.vision?.matches_created || 0) + (reconSummary.vision?.cached_matches_reinserted || 0)}</span>{reconSummary.vision?.cached_matches_reinserted ? <span className="text-slate-500"> ({reconSummary.vision.cached_matches_reinserted} cached)</span> : null}</div>
            <div>Research links: <span className="font-mono text-emerald-300">{reconSummary.research?.research_links_proposed || 0}</span></div>
            <div>Cost: <span className="font-mono text-emerald-300">£{(reconSummary.vision?.total_cost_gbp || 0).toFixed(4)}</span></div>
          </div>
          {reconSummary.vision?.orphan_links_purged ? (
            <div className="mt-2 text-[10px] text-amber-300/80">
              Cleaned {reconSummary.vision.orphan_links_purged} stale AI link(s) left over from a previous parser run.
            </div>
          ) : null}
        </div>
      )}

      {/* Red flags panel */}
      <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-orange-400" />
            <h3 className="text-sm font-semibold">Assessor Actions / Red Flags</h3>
            <span className="text-xs text-slate-500 font-mono">({active.length} open)</span>
          </div>
          <div className="flex gap-2 text-[10px] uppercase tracking-widest">
            {Object.entries(byCat).map(([c, n]) => (
              <span key={c} className={`border rounded-full px-1.5 py-0.5 ${CAT_STYLE[c] || ''}`}>
                {CAT_LABEL[c] || c}: {n}
              </span>
            ))}
          </div>
        </div>
        {active.length === 0 ? (
          <div className="text-sm text-slate-500 italic py-6 text-center">No open red flags — refresh to recompute after evidence changes.</div>
        ) : (
          <div className="divide-y divide-slate-800">
            {active.map((f) => (
              <div key={f.id} className="py-2.5 flex items-start gap-3" data-testid={`flag-${f.id}`}>
                <span className={`text-[9px] uppercase tracking-widest border rounded px-1.5 py-0.5 mt-0.5 ${CAT_STYLE[f.category] || ''}`}>
                  {CAT_LABEL[f.category] || f.category}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-slate-200 truncate">{f.title}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{f.detail}</div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => doResolve(f, 'accepted')} title="Accept as-is" data-testid={`flag-accept-${f.id}`}
                          className="p-1 rounded hover:bg-slate-800 text-emerald-400 hover:text-emerald-300">
                    <CheckCircle2 className="w-4 h-4" />
                  </button>
                  <button onClick={() => doResolve(f, 'dismissed')} title="Dismiss with rationale" data-testid={`flag-dismiss-${f.id}`}
                          className="p-1 rounded hover:bg-slate-800 text-slate-400 hover:text-slate-200">
                    <XCircle className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Report preview */}
      {preview && (
        <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold flex items-center gap-2">
              <FileDown className="w-4 h-4 text-cyan-400" /> Report preview
            </h3>
            <span className="text-xs text-slate-500 font-mono" data-testid="report-preview-summary">
              {(preview.placed?.length || 0) + (preview.register?.length || 0) + (preview.site_notes?.length || 0)} images · each once
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            {[
              ['Placed beside inputs (confirmed)', preview.placed?.length],
              ['Photo register (unconfirmed)', preview.register?.length],
              ['Site notes / plans', preview.site_notes?.length],
            ].map(([k, n]) => (
              <div key={k} className="rounded border border-slate-800 bg-slate-950 p-2">
                <div className="text-[10px] uppercase tracking-widest text-slate-500">{k}</div>
                <div className="text-lg font-mono text-slate-200">{n ?? 0}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Gold-standard comparison */}
      {compareResult && (
        <div className="rounded-lg border border-cyan-500/30 bg-cyan-500/5 p-4">
          <h3 className="text-sm font-semibold text-cyan-300 mb-2 flex items-center gap-2">
            <MessageSquare className="w-4 h-4" /> Gold-standard comparison
          </h3>
          <div className="text-xs text-slate-400 mb-2">
            Ref: {compareResult.reference_paragraphs}p / {compareResult.reference_tables}t
            &middot; Gen: {compareResult.generated_paragraphs}p / {compareResult.generated_tables}t
          </div>
          <div className="divide-y divide-slate-800 text-sm">
            {compareResult.rows.map((r) => (
              <div key={r.section} className="py-1.5 flex items-center gap-2">
                <span className={`text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded border ${
                  r.status === 'MATCH' ? 'border-emerald-500/50 text-emerald-300' :
                  r.status === 'DIFFERENT' ? 'border-amber-500/50 text-amber-300' :
                  r.status === 'MISSING' ? 'border-red-500/50 text-red-300' :
                  r.status === 'EXTRA' ? 'border-purple-500/50 text-purple-300' :
                  'border-slate-700 text-slate-500'
                }`}>{r.status}</span>
                <span className="text-slate-400 w-24 text-xs">{r.section}</span>
                <span className="text-xs text-slate-500 truncate flex-1">ref: {r.reference || '—'} &middot; gen: {r.generated || '—'}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};
